import { Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import { Suspense, useEffect } from "react";
import { lazyNamed } from "@/lib/lazyWithReload";
import { CoveringLoadingSpinner } from "@/components/LoadingSpinner/CoveringLoadingSpinner";
import { InlineLoadingSpinner } from "@/components/LoadingSpinner/InlineLoadingSpinner";
import { LoginPage } from "@/pages/auth/LoginPage";
import { RegisterPage } from "@/pages/auth/RegisterPage";
import { CompleteSignupPage } from "@/pages/auth/CompleteSignupPage";
import { OnboardingCoach } from "@/components/POnboarding/OnboardingCoach";
import { OnboardingPointer } from "@/components/POnboarding/OnboardingPointer";
import { ResetPasswordPage } from "@/pages/auth/ResetPasswordPage";
import { ActivatePage } from "@/pages/auth/ActivatePage";
import { CheckEmailPage } from "@/pages/auth/CheckEmailPage";
import { MfaVerifyPage } from "@/pages/auth/MfaVerifyPage";
import { MfaSetupPage } from "@/pages/auth/MfaSetupPage";
import { ChangePasswordPage } from "@/pages/auth/ChangePasswordPage";
import { PrivateRoute } from "@/lib/auth/PrivateRoute";
import { PublicOnlyRoute } from "@/lib/auth/PublicOnlyRoute";
import { RedirectKeepingQuery } from "@/lib/auth/RedirectKeepingQuery";
import { DashboardShell } from "@/components/DashboardShell";
import { SNotFound } from "@/components/Error/SNotFound";
import { LegalDocumentPage } from "@/pages/legal/LegalDocumentPage";
import { SenderVerifiedPage } from "@/pages/public/SenderVerifiedPage";
import { UserLayoutShell } from "@/components/UserLayoutShell";
import { EventLayoutShell } from "@/components/EventLayoutShell";
import { BuilderPreviewChrome } from "@/components/PBanner/BuilderPreviewChrome";
import { TemplateLayoutShell } from "@/components/TemplateLayoutShell";
import { OnboardingProvider } from "@/providers/OnboardingProvider";
import { EventSettingsAccessRedirect } from "@/pages/event/settings/access/EventSettingsAccessRedirect";

// All authenticated pages are lazy so the cold-load main bundle only
// carries auth + layout shells + small infrastructure (~250 KB gzip).
// Each route's chunk is fetched on first navigation and HTTP-cached.
const TemplatesPage = lazyNamed(() => import("@/pages/templates/TemplatesPage"), "TemplatesPage");
const DashboardRootPage = lazyNamed(() => import("@/pages/dashboard/DashboardRootPage"), "DashboardRootPage");
const CreateEventPage = lazyNamed(() => import("@/pages/dashboard/CreateEventPage"), "CreateEventPage");
const UserSecurityPage = lazyNamed(() => import("@/pages/user/UserSecurityPage"), "UserSecurityPage");
const UserSettingsPage = lazyNamed(() => import("@/pages/user/UserSettingsPage"), "UserSettingsPage");
const UserThemePage = lazyNamed(() => import("@/pages/user/UserThemePage"), "UserThemePage");
const UserCompanyPage = lazyNamed(() => import("@/pages/user/UserCompanyPage"), "UserCompanyPage");
const CompanyDpaPage = lazyNamed(() => import("@/pages/user/company/CompanyDpaPage"), "CompanyDpaPage");
const UserDomainsPage = lazyNamed(() => import("@/pages/user/UserDomainsPage"), "UserDomainsPage");
const CompanyPortalDomainPage = lazyNamed(() => import("@/pages/user/CompanyPortalDomainPage"), "CompanyPortalDomainPage");
const UserCollaboratorsPage = lazyNamed(() => import("@/pages/user/UserCollaboratorsPage"), "UserCollaboratorsPage");
const UserCrmPage = lazyNamed(() => import("@/pages/user/crm/UserCrmPage"), "UserCrmPage");
const UserPrivacyPage = lazyNamed(() => import("@/pages/user/privacy/UserPrivacyPage"), "UserPrivacyPage");
const UserSubscriptionPage = lazyNamed(() => import("@/pages/user/UserSubscriptionPage"), "UserSubscriptionPage");
const UserSubscriptionPlanPage = lazyNamed(() => import("@/pages/user/subscription/UserSubscriptionPlanPage"), "UserSubscriptionPlanPage");
const UserSubscriptionManagePage = lazyNamed(() => import("@/pages/user/subscription/UserSubscriptionManagePage"), "UserSubscriptionManagePage");
const UserSubscriptionGuestsPage = lazyNamed(() => import("@/pages/user/subscription/UserSubscriptionGuestsPage"), "UserSubscriptionGuestsPage");
const UserSubscriptionAiCreditsPage = lazyNamed(() => import("@/pages/user/subscription/UserSubscriptionAiCreditsPage"), "UserSubscriptionAiCreditsPage");
const UserSubscriptionSmsCreditsPage = lazyNamed(() => import("@/pages/user/subscription/UserSubscriptionSmsCreditsPage"), "UserSubscriptionSmsCreditsPage");
const UserSubscriptionCollaboratorsPage = lazyNamed(() => import("@/pages/user/subscription/UserSubscriptionCollaboratorsPage"), "UserSubscriptionCollaboratorsPage");
const UserSubscriptionInvoicesPage = lazyNamed(() => import("@/pages/user/subscription/UserSubscriptionInvoicesPage"), "UserSubscriptionInvoicesPage");
const UserSubscriptionEventAdminPage = lazyNamed(() => import("@/pages/user/subscription/UserSubscriptionEventAdminPage"), "UserSubscriptionEventAdminPage");
const UserDeleteAccountPendingPage = lazyNamed(() => import("@/pages/user/UserDeleteAccountPendingPage"), "UserDeleteAccountPendingPage");
const UserDeleteAccountFailedPage = lazyNamed(() => import("@/pages/user/UserDeleteAccountFailedPage"), "UserDeleteAccountFailedPage");
const EventHelpPage = lazyNamed(() => import("@/pages/event/EventHelpPage"), "EventHelpPage");
const EventShortcutsPage = lazyNamed(() => import("@/pages/event/EventShortcutsPage"), "EventShortcutsPage");
const EventSettingsGeneralPage = lazyNamed(() => import("@/pages/event/settings/EventSettingsGeneralPage"), "EventSettingsGeneralPage");
const EventSettingsRulesPage = lazyNamed(() => import("@/pages/event/settings/EventSettingsRulesPage"), "EventSettingsRulesPage");
const EventSettingsPluginsPage = lazyNamed(() => import("@/pages/event/settings/EventSettingsPluginsPage"), "EventSettingsPluginsPage");
const EventSettingsNotificationsPage = lazyNamed(() => import("@/pages/event/settings/EventSettingsNotificationsPage"), "EventSettingsNotificationsPage");
const EventAccessLayout = lazyNamed(() => import("@/pages/event/settings/access/EventAccessLayout"), "EventAccessLayout");
const EventAccessTabs = lazyNamed(() => import("@/pages/event/settings/access/EventAccessTabs"), "EventAccessTabs");
const EventMembersTab = lazyNamed(() => import("@/pages/event/settings/access/EventMembersTab"), "EventMembersTab");
const RolesPage = lazyNamed(() => import("@/pages/event/settings/access/RolesPage"), "RolesPage");
const ApiTokensTab = lazyNamed(() => import("@/pages/event/settings/access/ApiTokensTab"), "ApiTokensTab");
const RoleTemplateEditorPage = lazyNamed(() => import("@/pages/event/settings/access/RoleTemplateEditorPage"), "RoleTemplateEditorPage");
const EventInviteRoute = lazyNamed(() => import("@/pages/members/event/EventInviteRoute"), "EventInviteRoute");
const CompanyInviteRoute = lazyNamed(() => import("@/pages/members/company/CompanyInviteRoute"), "CompanyInviteRoute");
const CompanyMemberEditRoute = lazyNamed(() => import("@/pages/members/company/CompanyMemberEditRoute"), "CompanyMemberEditRoute");
const EventMemberEditRoute = lazyNamed(() => import("@/pages/members/event/EventMemberEditRoute"), "EventMemberEditRoute");
const EventDocumentsPage = lazyNamed(() => import("@/pages/event/EventDocumentsPage"), "EventDocumentsPage");
const EventContentProgrammePage = lazyNamed(() => import("@/pages/event/EventContentProgrammePage"), "EventContentProgrammePage");
const EventContentSpeakersPage = lazyNamed(() => import("@/pages/event/EventContentSpeakersPage"), "EventContentSpeakersPage");
const EventContentSponsorsPage = lazyNamed(() => import("@/pages/event/EventContentSponsorsPage"), "EventContentSponsorsPage");
const EventSettingsMediaPage = lazyNamed(() => import("@/pages/event/settings/EventSettingsMediaPage"), "EventSettingsMediaPage");
const EventTicketingPage = lazyNamed(() => import("@/pages/event/EventTicketingPage"), "EventTicketingPage");
const EventAttachmentsPage = lazyNamed(() => import("@/pages/event/EventAttachmentsPage"), "EventAttachmentsPage");
const EventAttachmentsSharedLinksPage = lazyNamed(() => import("@/pages/event/EventAttachmentsSharedLinksPage"), "EventAttachmentsSharedLinksPage");
const EventAutomationIndexPage = lazyNamed(() => import("@/pages/event/automation/EventAutomationIndexPage"), "EventAutomationIndexPage");
const EventAutomationWorkflowsPage = lazyNamed(() => import("@/pages/event/automation/EventAutomationWorkflowsPage"), "EventAutomationWorkflowsPage");
const EventAutomationHistoryPage = lazyNamed(() => import("@/pages/event/automation/EventAutomationHistoryPage"), "EventAutomationHistoryPage");
const EventGuestsGuestlistPage = lazyNamed(() => import("@/pages/event/guests/EventGuestsGuestlistPage"), "EventGuestsGuestlistPage");
const EventGuestsRequestsPage = lazyNamed(() => import("@/pages/event/guests/EventGuestsRequestsPage"), "EventGuestsRequestsPage");
const EventGuestsTrashPage = lazyNamed(() => import("@/pages/event/guests/EventGuestsTrashPage"), "EventGuestsTrashPage");
const EventGuestsSlottingRedirectPage = lazyNamed(() => import("@/pages/event/EventGuestsSlottingRedirectPage"), "EventGuestsSlottingRedirectPage");
const EventSeatingPage = lazyNamed(() => import("@/pages/event/EventSeatingPage"), "EventSeatingPage");
const EventCheckInDevicesPage = lazyNamed(() => import("@/pages/event/EventCheckInDevicesPage"), "EventCheckInDevicesPage");
const EventCheckInHistoryPage = lazyNamed(() => import("@/pages/event/EventCheckInHistoryPage"), "EventCheckInHistoryPage");
const EventMessagingSenderPage = lazyNamed(() => import("@/pages/event/messaging/EventMessagingSenderPage"), "EventMessagingSenderPage");
const EventMessagingMailTemplatesPage = lazyNamed(() => import("@/pages/event/messaging/EventMessagingMailTemplatesPage"), "EventMessagingMailTemplatesPage");
const EventMessagingSentMailsPage = lazyNamed(() => import("@/pages/event/messaging/EventMessagingSentMailsPage"), "EventMessagingSentMailsPage");
const EventSmsSentPage = lazyNamed(() => import("@/pages/event/sms/EventSmsSentPage"), "EventSmsSentPage");
const EventSmsTeamNumbersPage = lazyNamed(() => import("@/pages/event/sms/EventSmsTeamNumbersPage"), "EventSmsTeamNumbersPage");
const EventSmsTemplatesPage = lazyNamed(() => import("@/pages/event/sms/EventSmsTemplatesPage"), "EventSmsTemplatesPage");
const EventWebsitePage = lazyNamed(() => import("@/pages/event/EventWebsitePage"), "EventWebsitePage");
const EventDashboardPage = lazyNamed(() => import("@/pages/event/EventDashboardPage"), "EventDashboardPage");
const EventGoalsPage = lazyNamed(() => import("@/pages/event/EventGoalsPage"), "EventGoalsPage");
const EventAppPage = lazyNamed(() => import("@/pages/event/EventAppPage"), "EventAppPage");
const EventProgrammeOfferingPage = lazyNamed(() => import("@/pages/event/EventProgrammeOfferingPage"), "EventProgrammeOfferingPage");
const EventSlottingIndexRedirectPage = lazyNamed(() => import("@/pages/event/EventSlottingRedirectPage"), "EventSlottingIndexRedirectPage");
const EventSlottingOfferingRedirectPage = lazyNamed(() => import("@/pages/event/EventSlottingRedirectPage"), "EventSlottingOfferingRedirectPage");
const TemplateIndexPage = lazyNamed(() => import("@/pages/templates/template/TemplateIndexPage"), "TemplateIndexPage");
const TemplateGeneralPage = lazyNamed(() => import("@/pages/templates/template/TemplateGeneralPage"), "TemplateGeneralPage");
const TemplatePluginsPage = lazyNamed(() => import("@/pages/templates/template/TemplatePluginsPage"), "TemplatePluginsPage");
const TemplateRulesPage = lazyNamed(() => import("@/pages/templates/template/TemplateRulesPage"), "TemplateRulesPage");
const TemplateCollaboratorsPage = lazyNamed(() => import("@/pages/templates/template/TemplateCollaboratorsPage"), "TemplateCollaboratorsPage");
const TemplateWebsitePage = lazyNamed(() => import("@/pages/templates/template/TemplateWebsitePage"), "TemplateWebsitePage");
const TemplateFieldsPage = lazyNamed(() => import("@/pages/templates/template/TemplateFieldsPage"), "TemplateFieldsPage");
const TemplateSlottingPage = lazyNamed(() => import("@/pages/templates/template/TemplateSlottingPage"), "TemplateSlottingPage");
const TemplateDocumentsPage = lazyNamed(() => import("@/pages/templates/template/TemplateDocumentsPage"), "TemplateDocumentsPage");
const TemplateAttachmentsPage = lazyNamed(() => import("@/pages/templates/template/TemplateAttachmentsPage"), "TemplateAttachmentsPage");
const TemplateDevicesPage = lazyNamed(() => import("@/pages/templates/template/TemplateDevicesPage"), "TemplateDevicesPage");
const TemplateSeatingPage = lazyNamed(() => import("@/pages/templates/template/TemplateSeatingPage"), "TemplateSeatingPage");
const TemplateEmailPage = lazyNamed(() => import("@/pages/templates/template/TemplateEmailPage"), "TemplateEmailPage");
const TemplateSenderPage = lazyNamed(() => import("@/pages/templates/template/TemplateSenderPage"), "TemplateSenderPage");
const TemplateWorkflowsPage = lazyNamed(() => import("@/pages/templates/template/TemplateWorkflowsPage"), "TemplateWorkflowsPage");
const TemplateDashboardPage = lazyNamed(() => import("@/pages/templates/template/TemplateDashboardPage"), "TemplateDashboardPage");
const TemplateGoalsPage = lazyNamed(() => import("@/pages/templates/template/TemplateGoalsPage"), "TemplateGoalsPage");
const TemplateTableViewsPage = lazyNamed(() => import("@/pages/templates/template/TemplateTableViewsPage"), "TemplateTableViewsPage");
const TemplateGuestImportPage = lazyNamed(() => import("@/pages/templates/template/TemplateGuestImportPage"), "TemplateGuestImportPage");

// Builders pull heavy deps (immer/dompurify/builder-engine). Lazy so
// they don't bloat the initial bundle and so vite's dep-optimization
// pass doesn't trigger a full-reload mid-test on first auth-flow hit.
const WebsiteConfiguratorPage = lazyNamed(() => import("@/pages/builders/website/WebsiteConfiguratorPage"), "WebsiteConfiguratorPage");
const WebsiteEditorPage = lazyNamed(() => import("@/pages/builders/website/WebsiteEditorPage"), "WebsiteEditorPage");
const WebsiteTemplateConfiguratorPage = lazyNamed(() => import("@/pages/builders/website/WebsiteTemplateConfiguratorPage"), "WebsiteTemplateConfiguratorPage");
const WebsiteTemplateEditorPage = lazyNamed(() => import("@/pages/builders/website/WebsiteTemplateEditorPage"), "WebsiteTemplateEditorPage");
const EmailBuilderPage = lazyNamed(() => import("@/pages/builders/email/EmailBuilderPage"), "EmailBuilderPage");
const EmailTemplateBuilderPage = lazyNamed(() => import("@/pages/builders/email/EmailTemplateBuilderPage"), "EmailTemplateBuilderPage");
const DocumentBuilderPage = lazyNamed(() => import("@/pages/builders/document/DocumentBuilderPage"), "DocumentBuilderPage");
const DocumentTemplateBuilderPage = lazyNamed(() => import("@/pages/builders/document/DocumentTemplateBuilderPage"), "DocumentTemplateBuilderPage");
const WalletBuilderPage = lazyNamed(() => import("@/pages/builders/wallet/WalletBuilderPage"), "WalletBuilderPage");
const WalletTemplateBuilderPage = lazyNamed(() => import("@/pages/builders/wallet/WalletTemplateBuilderPage"), "WalletTemplateBuilderPage");
const CalendarBuilderPage = lazyNamed(() => import("@/pages/builders/calendar/CalendarBuilderPage"), "CalendarBuilderPage");
const SmsTemplateBuilderPage = lazyNamed(() => import("@/pages/builders/sms/SmsTemplateBuilderPage"), "SmsTemplateBuilderPage");
const CalendarTemplateBuilderPage = lazyNamed(() => import("@/pages/builders/calendar/CalendarTemplateBuilderPage"), "CalendarTemplateBuilderPage");
const PortalBuilderPage = lazyNamed(() => import("@/pages/builders/portal/PortalBuilderPage"), "PortalBuilderPage");
const GuestLayoutBuilderPage = lazyNamed(() => import("@/pages/builders/guest-layout/GuestLayoutBuilderPage"), "GuestLayoutBuilderPage");
const SpeakerLayoutBuilderPage = lazyNamed(() => import("@/pages/builders/speaker-layout/SpeakerLayoutBuilderPage"), "SpeakerLayoutBuilderPage");
const SponsorLayoutBuilderPage = lazyNamed(() => import("@/pages/builders/sponsor-layout/SponsorLayoutBuilderPage"), "SponsorLayoutBuilderPage");
const ProgramItemLayoutBuilderPage = lazyNamed(() => import("@/pages/builders/program-item-layout/ProgramItemLayoutBuilderPage"), "ProgramItemLayoutBuilderPage");
const EventAppBuilderPage = lazyNamed(() => import("@/pages/builders/app/EventAppBuilderPage"), "EventAppBuilderPage");
const EventAppPreviewPage = lazyNamed(() => import("@/pages/builders/app/EventAppPreviewPage"), "EventAppPreviewPage");
const EventAppSetupWizardPage = lazyNamed(() => import("@/pages/builders/app/EventAppSetupWizardPage"), "EventAppSetupWizardPage");

/**
 * Permanent redirect for legacy {@code /portal/:token} bookmarks.
 *
 * <p>The guest portal lives at {@code portal.veltric.app/p/:token} (or a
 * customer's configured custom domain). Cockpit only sees this URL shape
 * when someone has an old email link or bookmark — we redirect them to
 * the canonical host. Kept forever as a safety net; cost is negligible.
 *
 * <p>In e2e (vite mode=e2e) we don't run the portal SPA in
 * docker-compose, so the redirect would land on production. Render the
 * minimal local stand-in (PortalLocalPage) that talks directly to
 * main-backend's portal endpoint.
 */
const PORTAL_REDIRECT_HOST = "https://portal.veltric.app";
const PORTAL_LOCAL_MODE = import.meta.env.MODE === "e2e";

const PortalLocalPageLazy = lazyNamed(
  () => import("@/pages/portal/PortalLocalPage"),
  "PortalLocalPage",
);

function PortalRoute() {
  const { token = "" } = useParams<{ token: string }>();
  useEffect(() => {
    if (PORTAL_LOCAL_MODE) return;
    if (token) {
      window.location.replace(`${PORTAL_REDIRECT_HOST}/p/${encodeURIComponent(token)}`);
    }
  }, [token]);
  if (PORTAL_LOCAL_MODE) return <PortalLocalPageLazy />;
  return <CoveringLoadingSpinner />;
}

export default function App() {
  return (
    <>
    <Routes>
      <Route element={<PublicOnlyRoute />}>
        <Route path="/auth/login" element={<LoginPage />} />
        {/* Keeps the query: the login deep link (`?next=`) survives the alias
            (billing-comms Teil 2a; it used to be dropped here). */}
        <Route path="/login" element={<RedirectKeepingQuery to="/auth/login" />} />
        <Route path="/auth/register" element={<RegisterPage />} />
        <Route path="/auth/complete-signup" element={<CompleteSignupPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      </Route>

      <Route path="/auth/mfa-verify" element={<MfaVerifyPage />} />
      <Route path="/mfa-verify" element={<RedirectKeepingQuery to="/auth/mfa-verify" />} />
      <Route path="/auth/activate" element={<ActivatePage />} />
      <Route path="/auth/check-email" element={<CheckEmailPage />} />
      <Route path="/auth/change-password" element={<ChangePasswordPage />} />
      <Route path="/auth/mfa-setup" element={<MfaSetupPage />} />

      {/* Legal documents. PUBLIC and outside every auth gate on purpose: the
          consent checkboxes on the registration forms link here, and a person
          who has not signed up yet must be able to read what they are about to
          accept. Before this route existed both links fell through to the
          catch-all 404. */}
      <Route path="/legal/:doc" element={<LegalDocumentPage />} />

      {/* Landing page of the AWS sender-address confirmation link. Public and
          outside PublicOnlyRoute: the clicker may or may not be logged in, and
          either way must be told what happened. No API call. */}
      <Route path="/sender-verified" element={<SenderVerifiedPage />} />

      <Route path="/portal/:token" element={<PortalRoute />} />

      {/* Builders are full-screen editors with their own chrome — no
          AppBar/sidebar shell. They auth-gate but do not nest under
          DashboardShell or EventLayoutShell. Template variants are
          listed before event variants so RR's specificity ranking
          isn't relied upon for "template" vs ":eventId" disambiguation.
          OnboardingProvider is required because every builder page
          renders <OnboardingStepMarker> which calls useOnboarding(). */}
      <Route
        element={
          <PrivateRoute>
            <OnboardingProvider initialProgress={null}>
              <Suspense fallback={<CoveringLoadingSpinner />}>
                <div data-testid="page-root" className="contents">
                  {/* Builders live outside EventLayoutShell; this carries an
                      active "preview as role" session (banner + capabilities)
                      into them when one is open. Pass-through otherwise. */}
                  <BuilderPreviewChrome />
                </div>
              </Suspense>
            </OnboardingProvider>
          </PrivateRoute>
        }
      >
        <Route path="/website-builder/template/:templateId/configurator" element={<WebsiteTemplateConfiguratorPage />} />
        <Route path="/website-builder/template/:templateId/:slug" element={<WebsiteTemplateEditorPage />} />
        <Route path="/website-builder/:eventId/configurator" element={<WebsiteConfiguratorPage />} />
        <Route path="/website-builder/:eventId/:slug" element={<WebsiteEditorPage />} />

        <Route path="/email-builder/template/:templateId/:emailTemplateId/configurator" element={<EmailTemplateBuilderPage />} />
        <Route path="/email-builder/:eventId/:templateId/configurator" element={<EmailBuilderPage />} />

        <Route path="/document-builder/template/:templateId/:documentId/configurator" element={<DocumentTemplateBuilderPage />} />
        <Route path="/document-builder/:eventId/:documentId/configurator" element={<DocumentBuilderPage />} />

        <Route path="/wallet-builder/template/:templateId/:documentId/configurator" element={<WalletTemplateBuilderPage />} />
        <Route path="/wallet-builder/:eventId/:documentId/configurator" element={<WalletBuilderPage />} />

        <Route path="/calendar-builder/template/:templateId/:documentId/configurator" element={<CalendarTemplateBuilderPage />} />
        <Route path="/calendar-builder/:eventId/:documentId/configurator" element={<CalendarBuilderPage />} />

        <Route path="/sms-builder/:eventId/:smsTemplateId" element={<SmsTemplateBuilderPage />} />

        <Route path="/portal-builder/:eventId" element={<PortalBuilderPage />} />

        <Route path="/guest-layout-builder/:eventId/:layoutId?" element={<GuestLayoutBuilderPage />} />
        {/* Same page, same chunk — the create lanes only swap which document
            the builder edits (Event.detailLayouts.<surface>). */}
        <Route path="/guest-create-layout-builder/:eventId/:surface/:layoutId?" element={<GuestLayoutBuilderPage lane="create" />} />
        <Route path="/guest-request-layout-builder/:eventId/:surface/:layoutId?" element={<GuestLayoutBuilderPage lane="requestDetail" />} />
        <Route path="/speaker-layout-builder/:eventId/:layoutId?" element={<SpeakerLayoutBuilderPage />} />
        <Route path="/sponsor-layout-builder/:eventId/:layoutId?" element={<SponsorLayoutBuilderPage />} />
        <Route path="/program-item-layout-builder/:eventId/:layoutId?" element={<ProgramItemLayoutBuilderPage />} />
        <Route path="/app-builder/:eventId" element={<EventAppBuilderPage />} />
        <Route path="/app-builder/:eventId/setup" element={<EventAppSetupWizardPage />} />
        <Route path="/app-preview/:eventId" element={<EventAppPreviewPage />} />
      </Route>

      {/* /event/:eventId has its own layout (EventLayoutShell — sidebar +
          AppBar with eventId). Legacy split (routes)/(dashboard) and
          (routes)/event into separate layout segments, so they never
          composed; cockpit must mirror that — wrapping /event/* in
          DashboardShell would render two AppBars. */}
      <Route
        element={
          <PrivateRoute>
            <EventLayoutShell />
          </PrivateRoute>
        }
      >
        <Route
          element={
            <Suspense fallback={<InlineLoadingSpinner />}>
              <Outlet />
            </Suspense>
          }
        >
          <Route path="/event/:eventId/help" element={<EventHelpPage />} />
          <Route path="/event/:eventId/shortcuts" element={<EventShortcutsPage />} />
          <Route path="/event/:eventId/settings/general" element={<EventSettingsGeneralPage />} />
          <Route path="/event/:eventId/settings/rules" element={<EventSettingsRulesPage />} />
          <Route path="/event/:eventId/settings/plugins" element={<EventSettingsPluginsPage />} />
          <Route path="/event/:eventId/settings/notifications" element={<EventSettingsNotificationsPage />} />
          <Route element={<EventAccessLayout />}>
            {/* The access tabs (members wave U3): Members (a custom role's
                "My access"), Roles, API tokens — absolute paths, not index. */}
            <Route element={<EventAccessTabs />}>
              <Route path="/event/:eventId/settings/access" element={<EventMembersTab />} />
              <Route path="/event/:eventId/settings/access/roles" element={<RolesPage />} />
              <Route path="/event/:eventId/settings/access/tokens" element={<ApiTokensTab />} />
            </Route>
            <Route path="/event/:eventId/settings/access/invite" element={<EventInviteRoute />} />
            <Route path="/event/:eventId/settings/access/members/:eventRoleId" element={<EventMemberEditRoute />} />
            {/* The role editor (members wave U3): a new role, or one of the event's. */}
            <Route path="/event/:eventId/settings/access/roles/new" element={<RoleTemplateEditorPage />} />
            <Route path="/event/:eventId/settings/access/roles/:templateId" element={<RoleTemplateEditorPage />} />
          </Route>
          {/* Legacy bookmarks: collab + my-access land on the Members tab,
              role templates + API tokens on the tabs their dialogs became
              (a `?modal=` link is turned into the tab by EventAccessTabs). */}
          <Route path="/event/:eventId/settings/collaboration" element={<EventSettingsAccessRedirect />} />
          <Route path="/event/:eventId/settings/role-templates" element={<EventSettingsAccessRedirect tab="roles" />} />
          <Route path="/event/:eventId/settings/api-tokens" element={<EventSettingsAccessRedirect tab="tokens" />} />
          <Route path="/event/:eventId/settings/my-access" element={<EventSettingsAccessRedirect />} />
          <Route path="/event/:eventId/documents" element={<EventDocumentsPage />} />
          <Route path="/event/:eventId/programme" element={<EventContentProgrammePage />} />
          <Route path="/event/:eventId/programme/offering/:offeringId" element={<EventProgrammeOfferingPage />} />
          <Route path="/event/:eventId/speakers" element={<EventContentSpeakersPage />} />
          <Route path="/event/:eventId/sponsors" element={<EventContentSponsorsPage />} />
          <Route path="/event/:eventId/settings/media" element={<EventSettingsMediaPage />} />
          <Route path="/event/:eventId/ticketing" element={<EventTicketingPage />} />
          <Route path="/event/:eventId/attachments" element={<EventAttachmentsPage />} />
          <Route path="/event/:eventId/attachments/shared-links" element={<EventAttachmentsSharedLinksPage />} />
          <Route path="/event/:eventId/automation" element={<EventAutomationIndexPage />} />
          <Route path="/event/:eventId/automation/workflows" element={<EventAutomationWorkflowsPage />} />
          <Route path="/event/:eventId/automation/history" element={<EventAutomationHistoryPage />} />
          <Route path="/event/:eventId/guests/guestlist" element={<EventGuestsGuestlistPage />} />
          <Route path="/event/:eventId/guests/requests" element={<EventGuestsRequestsPage />} />
          <Route path="/event/:eventId/guests/trash" element={<EventGuestsTrashPage />} />
          <Route path="/event/:eventId/guests/slotting" element={<EventGuestsSlottingRedirectPage />} />
          <Route path="/event/:eventId/seating" element={<EventSeatingPage />} />
          <Route path="/event/:eventId/seating/:roomId" element={<EventSeatingPage />} />
          <Route path="/event/:eventId/check-in/devices" element={<EventCheckInDevicesPage />} />
          <Route path="/event/:eventId/check-in/history" element={<EventCheckInHistoryPage />} />
          <Route path="/event/:eventId/messaging/sender" element={<EventMessagingSenderPage />} />
          <Route path="/event/:eventId/messaging/mail-templates" element={<EventMessagingMailTemplatesPage />} />
          <Route path="/event/:eventId/messaging/sent-mails" element={<EventMessagingSentMailsPage />} />
          {/* The SMS module's front door (search row, old links) lands on its first page — no chunk of its own. */}
          <Route path="/event/:eventId/sms" element={<Navigate to="sent-sms" replace />} />
          <Route path="/event/:eventId/sms/sent-sms" element={<EventSmsSentPage />} />
          <Route path="/event/:eventId/sms/team-numbers" element={<EventSmsTeamNumbersPage />} />
          <Route path="/event/:eventId/sms/templates" element={<EventSmsTemplatesPage />} />
          <Route path="/event/:eventId/website" element={<EventWebsitePage />} />
          <Route path="/event/:eventId/dashboard" element={<EventDashboardPage />} />
          <Route path="/event/:eventId/goals" element={<EventGoalsPage />} />
          <Route path="/event/:eventId/app" element={<EventAppPage />} />
          {/* W4/F7: slotting lives under Programme now. The old routes stay as
              redirects — 5+ secondary navigators (dashboard cards, ⌘K, guest
              popups, onboarding, email deep links) still target them. */}
          <Route path="/event/:eventId/slotting" element={<EventSlottingIndexRedirectPage />} />
          <Route path="/event/:eventId/slotting/:offeringId" element={<EventSlottingOfferingRedirectPage />} />
        </Route>
      </Route>

      <Route
        element={
          <PrivateRoute>
            <DashboardShell />
          </PrivateRoute>
        }
      >
        <Route
          element={
            <Suspense fallback={<InlineLoadingSpinner />}>
              <Outlet />
            </Suspense>
          }
        >
          <Route path="/" element={<DashboardRootPage />} />
          <Route path="/create-event" element={<CreateEventPage />} />
          <Route path="/templates" element={<TemplatesPage />} />

          <Route path="/templates/:templateId" element={<TemplateLayoutShell />}>
            <Route index element={<TemplateIndexPage />} />
            <Route path="general" element={<TemplateGeneralPage />} />
            <Route path="plugins" element={<TemplatePluginsPage />} />
            <Route path="rules" element={<TemplateRulesPage />} />
            <Route path="collaborators" element={<TemplateCollaboratorsPage />} />
            <Route path="website" element={<TemplateWebsitePage />} />
            <Route path="fields" element={<TemplateFieldsPage />} />
            <Route path="slotting" element={<TemplateSlottingPage />} />
            <Route path="documents" element={<TemplateDocumentsPage />} />
            <Route path="attachments" element={<TemplateAttachmentsPage />} />
            <Route path="devices" element={<TemplateDevicesPage />} />
            <Route path="seating" element={<TemplateSeatingPage />} />
            <Route path="email" element={<TemplateEmailPage />} />
            <Route path="sender" element={<TemplateSenderPage />} />
            <Route path="workflows" element={<TemplateWorkflowsPage />} />
            <Route path="dashboard" element={<TemplateDashboardPage />} />
            <Route path="goals" element={<TemplateGoalsPage />} />
            <Route path="table-views" element={<TemplateTableViewsPage />} />
            <Route path="guest-import" element={<TemplateGuestImportPage />} />
          </Route>

          <Route path="/user" element={<UserLayoutShell />}>
            <Route index element={<Navigate to="/user/settings" replace />} />
            <Route path="settings" element={<UserSettingsPage />} />
            <Route path="security" element={<UserSecurityPage />} />
            <Route path="theme" element={<UserThemePage />} />
            <Route path="company" element={<UserCompanyPage />} />
            <Route path="company/dpa" element={<CompanyDpaPage />} />
            <Route path="domains" element={<UserDomainsPage />} />
            <Route path="portal-domain" element={<CompanyPortalDomainPage />} />
            <Route path="collaborators" element={<UserCollaboratorsPage />} />
            <Route path="collaborators/invite" element={<CompanyInviteRoute />} />
            <Route path="collaborators/:companyRoleId" element={<CompanyMemberEditRoute />} />
            <Route path="crm" element={<UserCrmPage />} />
            <Route path="privacy" element={<UserPrivacyPage />} />
            <Route path="subscription" element={<UserSubscriptionPage />} />
            <Route path="subscription/plan" element={<UserSubscriptionPlanPage />} />
            <Route path="subscription/manage" element={<UserSubscriptionManagePage />} />
            <Route path="subscription/guests" element={<UserSubscriptionGuestsPage />} />
            <Route path="subscription/ai-credits" element={<UserSubscriptionAiCreditsPage />} />
            <Route path="subscription/sms-credits" element={<UserSubscriptionSmsCreditsPage />} />
            <Route path="subscription/collaborators" element={<UserSubscriptionCollaboratorsPage />} />
            <Route path="subscription/invoices" element={<UserSubscriptionInvoicesPage />} />
            <Route path="subscription/event/:eventId" element={<UserSubscriptionEventAdminPage />} />
            <Route path="delete-account/pending" element={<UserDeleteAccountPendingPage />} />
            <Route path="delete-account/failed" element={<UserDeleteAccountFailedPage />} />
          </Route>

          <Route path="*" element={<SNotFound />} />
        </Route>
      </Route>
    </Routes>
    {/* Persistent hands-on onboarding coach — travels the whole first-run
        journey (dashboard → create-event wizard → real event). */}
    <OnboardingCoach />
    {/* Anchored pointer card for guided checklist steps — App-level so it
        survives the overlay's navigation to the target page. */}
    <OnboardingPointer />
    </>
  );
}
